import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Copy, Trash2, Edit, ChevronRight, FileText, 
  Check, X, Search, Filter, MoreVertical, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChecklistTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/checklist-templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      if (error.response?.status === 403) {
        toast.error('Bu sayfaya erişim yetkiniz yok');
        navigate('/');
      } else {
        toast.error('Şablonlar yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleMigrateDefaults = async () => {
    try {
      setMigrating(true);
      const response = await axios.post(`${API}/checklist-templates/migrate-defaults`, {}, { withCredentials: true });
      toast.success(response.data.message);
      loadTemplates();
    } catch (error) {
      toast.error('Varsayılan şablonlar aktarılamadı');
    } finally {
      setMigrating(false);
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/checklist-templates/${templateId}/duplicate`, {}, { withCredentials: true });
      toast.success('Şablon kopyalandı');
      setTemplates([response.data, ...templates]);
    } catch (error) {
      toast.error('Şablon kopyalanamadı');
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    
    try {
      await axios.delete(`${API}/checklist-templates/${templateToDelete}`, { withCredentials: true });
      toast.success('Şablon silindi');
      setTemplates(templates.filter(t => t.template_id !== templateToDelete));
    } catch (error) {
      toast.error('Şablon silinemedi');
    } finally {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = stageFilter === 'all' || template.stage === parseInt(stageFilter);
    return matchesSearch && matchesStage;
  });

  const getCriteriaCount = (template) => {
    return template.sections?.reduce((acc, section) => acc + (section.criteria?.length || 0), 0) || 0;
  };

  const getSectionCount = (template) => {
    return template.sections?.length || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kontrol Listesi Şablonları</h1>
          <p className="text-gray-500 mt-1">Denetim kontrol listesi şablonlarını yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleMigrateDefaults}
            disabled={migrating}
            className="gap-2"
          >
            <Database className="w-4 h-4" />
            {migrating ? 'Aktarılıyor...' : 'Varsayılanları Aktar'}
          </Button>
          <Button
            onClick={() => navigate('/checklist-templates/new')}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Şablon
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Şablon ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Aşama Filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Aşamalar</SelectItem>
            <SelectItem value="2">2. Aşama (TR-I)</SelectItem>
            <SelectItem value="3">3. Aşama (GSTC)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Şablon Bulunamadı</h3>
          <p className="text-gray-500 mb-4">
            {templates.length === 0 
              ? 'Henüz kontrol listesi şablonu oluşturulmamış. Varsayılan şablonları aktararak başlayabilirsiniz.'
              : 'Arama kriterlerinize uygun şablon bulunamadı.'}
          </p>
          {templates.length === 0 && (
            <Button onClick={handleMigrateDefaults} disabled={migrating} className="gap-2">
              <Database className="w-4 h-4" />
              Varsayılan Şablonları Aktar
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.template_id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                    {template.is_default && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Varsayılan</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">{template.description}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/checklist-templates/${template.template_id}`)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Düzenle
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDuplicate(template.template_id)}>
                      <Copy className="w-4 h-4 mr-2" />
                      Kopyala
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => {
                        setTemplateToDelete(template.template_id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Sil
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="text-xs">
                  {template.stage}. Aşama
                </Badge>
                <Badge variant="outline" className="text-xs">
                  v{template.version}
                </Badge>
                {template.is_active ? (
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <Check className="w-3 h-3 mr-1" />
                    Aktif
                  </Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-700 text-xs">
                    <X className="w-3 h-3 mr-1" />
                    Pasif
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-3">
                <span>{getSectionCount(template)} bölüm</span>
                <span>{getCriteriaCount(template)} kriter</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-emerald-600 hover:text-emerald-700 gap-1 p-0"
                  onClick={() => navigate(`/checklist-templates/${template.template_id}`)}
                >
                  Düzenle
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Şablonu Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu şablonu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
